import React, { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BodyText,
  Button,
  Card,
  Portal,
  useOnClickOutside,
} from '@chargify/custom-ui-components';

import {
  FiltersWrapper,
  StatusFilter,
  InvoiceNumberFilter,
  ProductNameFilter,
  DateFilter,
} from './components';
import { Wrapper } from './Filters.styles';

import { ProductsContainer } from '../../containers';

import {
  FilterValue,
  FilterType,
  FiltersSettings,
} from '../../machines/billingFilters';

import { FILTERS_WIDTH } from './constants';
import { DateFilterTabType } from '../../machines/billingFilters/types';

export type FiltersProps = {
  /**
   * Indicator that filters dropdown is open
   */
  isOpen: boolean;
  /**
   * Indicator that at least one filter is applied
   */
  isFilteringApplied: boolean;
  /**
   * Update filter value event handler
   */
  onUpdateFilter: (filter: FilterType, value: FilterValue) => void;
  /**
   * Clear filter settings event handler
   */
  onClear: () => void;
  /**
   * Apply current filter settings event handler
   */
  onApply: () => void;
  /**
   * Current inline filter settings
   */
  filtersSettings: FiltersSettings;
  /**
   * Toogle filters dropdown visibility event handler
   */
  onToggleFilters: () => void;
  /**
   * Close filters event handler
   */
  onCloseFilters: () => void;
  /**
   * Toogle filter section
   */
  onToggleSection: (sectionName: FilterType) => void;
  /**
   * Filter section visibility state
   */
  filtersSections: Record<FilterType, boolean>;
  /**
   * Update filters date type event handler
   */
  onDateTypeChange: (dateType: DateFilterTabType) => void;
};

const Filters = ({
  filtersSettings,
  filtersSections,
  isOpen,
  isFilteringApplied,
  onUpdateFilter,
  onToggleFilters,
  onToggleSection,
  onApply,
  onClear,
  onDateTypeChange,
  onCloseFilters,
}: FiltersProps) => {
  const { t } = useTranslation('billing-history');

  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const portalContainerRef = useRef(null);

  useEffect(() => {
    return () => onCloseFilters();
  }, []);

  useOnClickOutside(
    [containerRef, dropdownRef, portalContainerRef],
    onCloseFilters
  );

  const calculateDropdownPosition = useCallback(() => {
    const { current: element } = containerRef;
    const { top, left, width, height } = element.getBoundingClientRect();

    return {
      top: top + height + window.scrollY,
      left: left - (FILTERS_WIDTH - width),
      width: FILTERS_WIDTH,
    };
  }, []);

  return (
    <Wrapper ref={containerRef}>
      <Button
        data-testid="filter-button"
        onClick={onToggleFilters}
        iconLeft="filter"
        size="medium"
        color={isFilteringApplied ? 'primary' : 'secondary'}
      />
      <Portal>
        {isOpen && (
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              ...(containerRef.current ? calculateDropdownPosition() : {}),
            }}
          >
            <Card variant="small">
              <Card.Header headerVariant="small">
                <BodyText variant={'body2'} fontWeight={600}>
                  {t('filter_invoices')}
                </BodyText>
              </Card.Header>
              <Card.Body bodyVariant="small">
                <FiltersWrapper>
                  <DateFilter
                    header={t('date_filters')}
                    portalContainer={portalContainerRef}
                    onClick={() => onToggleSection('date')}
                    isExpanded={filtersSections.date}
                    onChange={(date) => onUpdateFilter('date', date)}
                    values={filtersSettings.date}
                    onTabChange={onDateTypeChange}
                  />
                  <StatusFilter
                    header={t('status')}
                    onClick={() => onToggleSection('status')}
                    isExpanded={filtersSections.status}
                    values={filtersSettings.status}
                    onChange={(status) => onUpdateFilter('status', status)}
                  />
                  <ProductsContainer>
                    {({ products, showSearch }) => (
                      <ProductNameFilter
                        placeholder={t('search_product')}
                        showSearch={showSearch}
                        values={filtersSettings.productIds}
                        onChange={(v) => onUpdateFilter('productIds', v)}
                        products={products}
                        isExpanded={filtersSections.productIds}
                        onClick={() => onToggleSection('productIds')}
                        header={t('product_name')}
                      />
                    )}
                  </ProductsContainer>
                  <InvoiceNumberFilter
                    placeholder={t('search_invoice')}
                    value={filtersSettings.invoiceNumber}
                    onChange={(v) => onUpdateFilter('invoiceNumber', v)}
                    isExpanded={filtersSections.invoiceNumber}
                    onClick={() => onToggleSection('invoiceNumber')}
                    header={t('invoice_number')}
                  />
                </FiltersWrapper>
              </Card.Body>
              <Card.Footer footerVariant="small">
                <Button.Group alignment="right">
                  <Button
                    label={t('reset', { ns: 'common' })}
                    size={'small'}
                    color={'secondary'}
                    onClick={onClear}
                  />
                  <Button
                    label={t('apply', { ns: 'common' })}
                    size={'small'}
                    color={'primary'}
                    onClick={onApply}
                  />
                </Button.Group>
              </Card.Footer>
            </Card>
          </div>
        )}
        <div
          ref={portalContainerRef}
          style={{ position: 'absolute', top: 0, left: 0 }}
        />
      </Portal>
    </Wrapper>
  );
};

export default Filters;
